import { useParams } from "@tanstack/react-router";
import React, { useState } from "react";

// Import UI components
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { JsonEditor } from "@repo/ui/json-editor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { Textarea } from "@repo/ui/textarea";

// Import forms UI
import { TypeformStyle } from "@repo/forms-ui";

// Form configuration type (matching backend)
interface FormConfig {
	id: string;
	org: string;
	slug: string;
	title: string;
	description?: string;
	sections: FormSection[];
	submitButtonText?: string;
	successMessage?: string;
	redirectUrl?: string;
}

interface FormSection {
	id: string;
	title?: string;
	description?: string;
	fields: FormField[];
}

interface FormField {
	id: string;
	label?: string;
	type:
		| "textarea"
		| "text"
		| "email"
		| "phone"
		| "number"
		| "date"
		| "select"
		| "radio"
		| "checkbox"
		| "file";
	required?: boolean;
	placeholder?: string;
	helpText?: string;
	options?: Array<{ value: string; label: string }>;
	multiple?: boolean;
	accept?: string;
	maxSizeMb?: number;
}

export default function FormBuilder() {
	const { org, slug } = useParams({ from: "/forms/$org/$slug/builder" });
	const orgSafe = org ?? "";
	const slugSafe = slug ?? "";

	const [formConfig, setFormConfig] = useState<FormConfig>({
		id: `${orgSafe}-${slugSafe}`,
		org: orgSafe,
		slug: slugSafe,
		title: "",
		description: "",
		sections: [
			{
				id: "section-1",
				title: "General Information",
				fields: [
					{
						id: "field-1",
						label: "Full Name",
						type: "text",
						required: true,
						placeholder: "Enter your full name",
					},
				],
			},
		],
		submitButtonText: "Submit Form",
		successMessage: "Thank you for your submission!",
	});

	const [jsonError, setJsonError] = useState<string>("");
	const [isPreviewMode, setIsPreviewMode] = useState(false);

	const handleJsonChange = (jsonString: string) => {
		try {
			const parsed = JSON.parse(jsonString);
			setFormConfig(parsed);
			setJsonError("");
		} catch (error) {
			setJsonError(error instanceof Error ? error.message : "Invalid JSON");
		}
	};

	const handleMetadataChange = (field: keyof FormConfig, value: string) => {
		setFormConfig((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const addField = (sectionId: string) => {
		setFormConfig((prev) => ({
			...prev,
			sections: prev.sections.map((section) =>
				section.id === sectionId
					? {
							...section,
							fields: [
								...section.fields,
								{
									id: `field-${Date.now()}`,
									label: "New Field",
									type: "text",
									required: false,
								},
							],
						}
					: section,
			),
		}));
	};

	const updateField = (
		sectionId: string,
		fieldId: string,
		updates: Partial<FormField>,
	) => {
		setFormConfig((prev) => ({
			...prev,
			sections: prev.sections.map((section) =>
				section.id === sectionId
					? {
							...section,
							fields: section.fields.map((field) =>
								field.id === fieldId ? { ...field, ...updates } : field,
							),
						}
					: section,
			),
		}));
	};

	const removeField = (sectionId: string, fieldId: string) => {
		setFormConfig((prev) => ({
			...prev,
			sections: prev.sections.map((section) =>
				section.id === sectionId
					? {
							...section,
							fields: section.fields.filter((field) => field.id !== fieldId),
						}
					: section,
			),
		}));
	};

	const handleSave = () => {
		// TODO: Implement save functionality when API is available
		console.log("Saving form config:", formConfig);
		alert(
			"Save functionality will be implemented when the form management API is ready.",
		);
	};

	const handlePublish = () => {
		// TODO: Implement publish functionality when API is available
		console.log("Publishing form config:", formConfig);
		alert(
			"Publish functionality will be implemented when the form management API is ready.",
		);
	};

	return (
		<div className="container mx-auto px-4 py-8">
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-gray-900 mb-2">Form Builder</h1>
				<p className="text-gray-600">
					Build and configure forms for {org}/{slug}
				</p>
			</div>

			<div className="flex gap-6">
				{/* Builder Panel */}
				<div className="flex-1">
					<Tabs defaultValue="metadata" className="w-full">
						<TabsList className="grid w-full grid-cols-3">
							<TabsTrigger value="metadata">Metadata</TabsTrigger>
							<TabsTrigger value="fields">Fields</TabsTrigger>
							<TabsTrigger value="json">JSON Editor</TabsTrigger>
						</TabsList>

						<TabsContent value="metadata" className="space-y-6">
							<Card>
								<CardHeader>
									<CardTitle>Form Metadata</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div>
										<label
											htmlFor="form-title"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Title
										</label>
										<Input
											id="form-title"
											value={formConfig.title}
											onChange={(e) =>
												handleMetadataChange("title", e.target.value)
											}
											placeholder="Enter form title"
										/>
									</div>
									<div>
										<label
											htmlFor="form-description"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Description
										</label>
										<Textarea
											id="form-description"
											value={formConfig.description || ""}
											onChange={(e) =>
												handleMetadataChange("description", e.target.value)
											}
											placeholder="Enter form description"
											rows={3}
										/>
									</div>
									<div>
										<label
											htmlFor="form-submit-text"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Submit Button Text
										</label>
										<Input
											id="form-submit-text"
											value={formConfig.submitButtonText || ""}
											onChange={(e) =>
												handleMetadataChange("submitButtonText", e.target.value)
											}
											placeholder="Submit"
										/>
									</div>
									<div>
										<label
											htmlFor="form-success-message"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											Success Message
										</label>
										<Textarea
											id="form-success-message"
											value={formConfig.successMessage || ""}
											onChange={(e) =>
												handleMetadataChange("successMessage", e.target.value)
											}
											placeholder="Thank you for your submission!"
											rows={2}
										/>
									</div>
								</CardContent>
							</Card>
						</TabsContent>

						<TabsContent value="fields" className="space-y-6">
							{formConfig.sections.map((section, sectionIndex) => (
								<Card key={section.id}>
									<CardHeader>
										<CardTitle className="flex items-center justify-between">
											<span>
												{section.title || `Section ${sectionIndex + 1}`}
											</span>
											<Button
												variant="outline"
												size="sm"
												onClick={() => addField(section.id)}
											>
												Add Field
											</Button>
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="space-y-4">
											{section.fields.map((field) => (
												<div key={field.id} className="border rounded-lg p-4">
													<div className="flex items-start justify-between mb-4">
														<div className="flex-1 grid grid-cols-2 gap-4">
															<div>
																<label
																	htmlFor={`field-${field.id}-label`}
																	className="block text-sm font-medium text-gray-700 mb-1"
																>
																	Label
																</label>
																<Input
																	id={`field-${field.id}-label`}
																	value={field.label || ""}
																	onChange={(e) =>
																		updateField(section.id, field.id, {
																			label: e.target.value,
																		})
																	}
																	placeholder="Field label"
																/>
															</div>
															<div>
																<label
																	htmlFor={`field-${field.id}-type`}
																	className="block text-sm font-medium text-gray-700 mb-1"
																>
																	Type
																</label>
																<select
																	id={`field-${field.id}-type`}
																	value={field.type}
																	onChange={(e) =>
																		updateField(section.id, field.id, {
																			type: e.target.value as FormField["type"],
																		})
																	}
																	className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
																>
																	<option value="text">Text</option>
																	<option value="textarea">Textarea</option>
																	<option value="email">Email</option>
																	<option value="select">Select</option>
																	<option value="checkbox">Checkbox</option>
																	<option value="file">File</option>
																</select>
															</div>
														</div>
														<div className="flex items-center gap-2 ml-4">
															<label className="flex items-center gap-2 text-sm">
																<input
																	type="checkbox"
																	checked={field.required || false}
																	onChange={(e) =>
																		updateField(section.id, field.id, {
																			required: e.target.checked,
																		})
																	}
																/>
																Required
															</label>
															<Button
																variant="outline"
																size="sm"
																onClick={() =>
																	removeField(section.id, field.id)
																}
																className="text-red-600 hover:text-red-700"
															>
																Remove
															</Button>
														</div>
													</div>
													<div>
														<label
															htmlFor={`field-${field.id}-placeholder`}
															className="block text-sm font-medium text-gray-700 mb-1"
														>
															Placeholder
														</label>
														<Input
															id={`field-${field.id}-placeholder`}
															value={field.placeholder || ""}
															onChange={(e) =>
																updateField(section.id, field.id, {
																	placeholder: e.target.value,
																})
															}
															placeholder="Field placeholder"
														/>
													</div>
												</div>
											))}
											{section.fields.length === 0 && (
												<div className="text-center py-8 text-gray-500">
													No fields in this section. Click "Add Field" to get
													started.
												</div>
											)}
										</div>
									</CardContent>
								</Card>
							))}
						</TabsContent>

						<TabsContent value="json">
							<Card>
								<CardHeader>
									<CardTitle>JSON Configuration</CardTitle>
									{jsonError && (
										<div className="text-red-600 text-sm mt-2">{jsonError}</div>
									)}
								</CardHeader>
								<CardContent>
									<JsonEditor
										value={JSON.stringify(formConfig, null, 2)}
										onChange={handleJsonChange}
										height="600px"
									/>
								</CardContent>
							</Card>
						</TabsContent>
					</Tabs>

					{/* Action Buttons */}
					<div className="flex justify-end gap-4 mt-8">
						<Button
							variant="outline"
							onClick={() => setIsPreviewMode(!isPreviewMode)}
						>
							{isPreviewMode ? "Hide Preview" : "Show Preview"}
						</Button>
						<Button variant="outline" onClick={handleSave}>
							Save Draft
						</Button>
						<Button onClick={handlePublish}>Publish Form</Button>
					</div>
				</div>

				{/* Preview Panel */}
				{isPreviewMode && (
					<div className="w-96">
						<Card className="sticky top-4">
							<CardHeader>
								<CardTitle className="flex items-center justify-between">
									Live Preview
									<Badge variant="secondary">Preview</Badge>
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
									<TypeformStyle
										config={formConfig}
											onSubmit={(data) => {
												console.log("Form submitted:", data);
												alert("Form submission preview - check console for data");
												return undefined;
											}}
									/>
								</div>
							</CardContent>
						</Card>
					</div>
				)}
			</div>
		</div>
	);
}
